/**
 * A run of consecutive training days, one mark each, under a legend that names every state in words.
 *
 * Promoted to `shared/ui` on 1 September 2026 on its SECOND consumer: the roster's preview panel
 * drew this week, and the client detail's FREKWENCJA card now draws the same one. The owner's
 * instruction is that the two speak the same language — so the drawings, the legend words and the
 * hover readout are all HERE, and neither surface can restate them slightly differently. The Trendy
 * tab's thirty-cell grid is a different shape and keeps its own markup; it shares these tokens and,
 * since this round, these state words.
 *
 * ── The WINDOW is the caller's, and it is a COLUMN COUNT ───────────────────────────────────────
 * `columns` is the span the caller asked its source for; `days` is what came back. They are two
 * numbers because a read can be short — a client the server has fourteen days of history for gets
 * fourteen marks, one it has three for gets three, and both draw on the same fourteen-column track
 * so the second is visibly a partial answer rather than three days stretched across the card.
 *
 * The roster panel asks for seven, because its own copy says "ostatnie 7 dni". The client detail's
 * card asks for fourteen: the figure above the strip counts a fortnight, and a strip counting a
 * week 40px under it was two windows on one card with nothing saying they were different. Owner,
 * 1 September 2026: "zróbmy ostatnie 14 dni, skoro wyżej jest »4 z 7 sesji w ostatnich 14 dniach«".
 *
 * ── One ROW, and what that costs the captions ──────────────────────────────────────────────────
 * Fourteen marks wrap to two rows of seven far more legibly than they fit in one, and the owner
 * looked at both: "zrób te ostatnie 14 dni jako pasek koło siebie, a nie jeden pod drugim." One
 * row it is, and the weekday captions go with the decision rather than being carried into it —
 * fourteen letters at that column width are noise, and Pn…Nd printed twice orders nothing.
 *
 * What replaces them is the Trendy grid's answer to the same problem: a date under the first cell,
 * under the last, and under each Monday between them. A handful of readable anchors beats fourteen
 * unreadable letters, and the pointer answers any cell in between — which is why `title` on every
 * mark is load-bearing here rather than a nicety.
 *
 * Captions live in their own row rather than inside the marks, so a cell that carries one is
 * exactly as tall as a cell that does not.
 *
 * ── `role="img"`, and the name carries the shape ───────────────────────────────────────────────
 * Fourteen marks read out one at a time is a list nobody wants; what a reader needs from this strip
 * is the shape of the window, which is what `label` says in a sentence. The role hides the subtree,
 * so the captions and the per-cell readout are for the eye and the pointer — the counts in the name
 * are the same fact for everybody else. The caption row is a SIBLING of the grid and so is hidden
 * explicitly.
 */

import { useTranslation } from 'react-i18next';

import { toIsoDate } from '~/shared/lib/calendarDate';

import styles from './TrainingWeekStrip.module.css';

/**
 * What one calendar day amounted to.
 *
 * `planned` is a session still ahead of the client. Not every source can express it — see the
 * `states` prop — and a source that cannot must not draw a legend entry that can never appear.
 */
export type TrainingDayState = 'done' | 'missed' | 'planned' | 'free';

export interface TrainingDayMark {
  /** `YYYY-MM-DD`. Read at UTC midnight — a calendar date's day is not a zone question. */
  readonly date: string;
  /** Monday-first weekday index, 0–6. */
  readonly weekday: number;
  readonly state: TrainingDayState;
}

/** One class per state. A table, so a fifth state cannot be half-added. */
const CELL_CLASS: Readonly<Record<TrainingDayState, string>> = {
  done: String(styles.cellDone),
  missed: String(styles.cellMissed),
  planned: String(styles.cellPlanned),
  free: String(styles.cellFree),
};

/**
 * How the columns are named underneath.
 *
 * `weekday` prints Pn…Nd under every column and is only readable while there are seven of them.
 * `dates` prints a date under the ends and under each Monday, which is what a longer window can
 * carry. `ends` names the two ends alone, the newest as „dziś" when it is today — the client
 * detail's fortnight (KANON „Karty zadań", 24 September 2026). Named modes rather than a rule
 * derived from `columns`, because which one reads better is the caller's judgement about its own
 * card and not something to infer from a number.
 */
export type TrainingStripCaptions = 'weekday' | 'dates' | 'ends';

/**
 * How far from either end a Monday still earns its own date.
 *
 * A date is about two columns wide at a fortnight's column width, so a Monday one cell in from the
 * end would print on top of the end's own label. The end labels are the ones that cannot be
 * dropped — they are what says which fortnight this is — so a crowded Monday yields to them.
 */
const ANCHOR_CLEARANCE = 2;

interface TrainingWeekStripProps {
  /**
   * The window, oldest first, as consecutive calendar days. Never more than `columns`; fewer draws
   * fewer marks rather than padding a lie.
   */
  readonly days: readonly TrainingDayMark[];
  /** How many days the caller ASKED for. The track is this wide whatever came back. */
  readonly columns: number;
  /** Which captions go under the columns. See `TrainingStripCaptions`. */
  readonly captions: TrainingStripCaptions;
  /**
   * The states the legend names, in the order it names them.
   *
   * Only what the caller's own source can emit: `/consistency` has no word for a session still
   * ahead of the client, so the client detail passes three and the calendar-backed roster four. A
   * legend entry that can never appear is a question the reader cannot answer.
   */
  readonly states: readonly TrainingDayState[];
  /** The strip's accessible name — the caller's window and its counts, in the caller's words. */
  readonly label: string;
}

export function TrainingWeekStrip({
  days,
  columns,
  captions,
  states,
  label,
}: TrainingWeekStripProps) {
  const { t, i18n } = useTranslation();
  // `UTC`, because a `YYYY-MM-DD` is a calendar date and parsing the bare date IS the UTC-midnight
  // parse. Converting it into the browser's zone would move a mark onto the previous day.
  const dayDate = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const format = (date: string) => dayDate.format(new Date(`${date}T00:00:00Z`));

  const last = days.length - 1;
  const track = { gridTemplateColumns: `repeat(${String(Math.max(1, columns))}, minmax(0, 1fr))` };

  return (
    <div className={styles.strip}>
      <div className={styles.calendar}>
        <div className={styles.grid} role="img" aria-label={label} style={track}>
          {days.map((day) => (
            /* The readout the pointer gets: which day, and what it was, in words. With the weekday
             * captions gone this is the only way to name a cell between two anchors, and the fill
             * alone was the whole of the old answer — the owner's words for that were that he could
             * not read the diagram. */
            <span
              className={`${String(styles.cell)} ${CELL_CLASS[day.state]}`}
              key={day.date}
              title={t('web:trainingWeek.cell', {
                date: format(day.date),
                state: t(`web:trainingWeek.state.${day.state}`),
              })}
            />
          ))}
        </div>

        {/* Outside the grid of marks, so a cell is the same height whether or not a caption sits
          * under it, and hidden from the accessibility tree, because `label` already carries the
          * shape as a sentence. */}
        <div aria-hidden="true" className={styles.captions} style={track}>
          {days.map((day, index) => {
            const isEnd = index === 0 || index === last;
            const isMonday =
              day.weekday === 0 &&
              index >= ANCHOR_CLEARANCE &&
              index <= last - ANCHOR_CLEARANCE;
            if (captions === 'weekday') {
              return (
                <span className={styles.caption} key={day.date}>
                  {t(`web:trainingWeek.dow.${String(day.weekday)}`)}
                </span>
              );
            }
            if (!isEnd && (captions === 'ends' || !isMonday)) return <span key={day.date} />;
            return (
              <span
                className={`${String(styles.caption)} ${String(styles.captionDate)} ${
                  index === last ? String(styles.captionEnd) : ''
                }`}
                key={day.date}
              >
                {captions === 'ends' && index === last && day.date === toIsoDate(new Date())
                  ? t('web:trainingWeek.today')
                  : format(day.date)}
              </span>
            );
          })}
        </div>
      </div>

      <ul className={styles.legend} aria-label={t('web:trainingWeek.legendLabel')}>
        {states.map((state) => (
          <li className={styles.legendItem} key={state}>
            {/* The swatch wears the CELL's own classes. A legend that redraws its marks is a legend
              * that can disagree with the thing it explains. */}
            <span
              aria-hidden="true"
              className={`${String(styles.cell)} ${CELL_CLASS[state]} ${String(styles.legendSwatch)}`}
            />
            {t(`web:trainingWeek.state.${state}`)}
          </li>
        ))}
      </ul>
    </div>
  );
}
